import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  ElementRef,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { CommonStandaloneImports } from '../../shared/common/common-standalone-imports';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-ag-grid-table',
  imports: [...CommonStandaloneImports, AgGridModule],
  templateUrl: './ag-grid-table.html',
  styleUrl: './ag-grid-table.scss',
})
export class AgGridTableComponent implements OnInit {
  readonly PAGE_KEY = 'agGrid_current_page';

  @Input() subTitle: any;
  @Input() rowData: any[] = [];
  @Input() columnDefs: ColDef[] = [];
  @Input() paginationPageSize = 10;
  @Input() tooltipShowDelay = 0;
  @Input() tooltipHideDelay = 2000;
  @Input() headerHeight = 50;
  @Input() rowHeight = 50;
  @Input() parentContext: any;
  @Input() currentRowSelectedIndex: any;

  @Output() rowClicked = new EventEmitter<any>();
  @Output() gridReady = new EventEmitter<any>();
  @Output() reloadTable = new EventEmitter<any>();

  @ViewChild('agGrid') agGrid!: AgGridAngular;
  @ViewChild('filtertextbox') filtertextbox!: ElementRef;

  gridApi: any;
  gridColumnApi: any;
  currentRoute: any;
  totalPage = 0;
  currPage = 1;
  paging: number[] = [];
  searchedRow = 0;
  noRecordsFound = false;
  private isPageRestored = false;

  gridOptions: GridOptions = {
    defaultColDef: {
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset'],
        resetButton: true,
      },
    },
    localeText: {
      filterOoo: 'Filter...',
      resetFilter:
        '<img class="filter_icon" src="assets/images/filter-clear.svg" alt="">Clear',
    },
    suppressScrollOnNewData: true,
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    suppressRowClickSelection: true,
    suppressDragLeaveHidesColumns: true,
    groupSelectsChildren: true,
    pagination: true,
    paginationPageSize: 10,
    paginateChildRows: true,
    overlayNoRowsTemplate: `
      <div style="display:flex;justify-content:center;align-items:center;">
        <div style="padding:16px 14px;background:#fff;
        box-shadow:0 4px 14px rgba(105,95,151,.18);
        border-radius:8px;font-size:16px;">
          No records to show
        </div>
      </div>
    `,
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    this.currentRoute = this.router.url;
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
      });
    this.gridOptions = {
      ...this.gridOptions,
      context: {
        componentParent: this.parentContext,
      },
    };
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridReady.emit(params);
  }

  onFilterTextBoxChanged() {
    const filterText = this.filtertextbox.nativeElement.value;
    this.gridApi?.setQuickFilter(filterText);

    setTimeout(() => this.onPaginationChanged(), 100);
  }

  onFilterApplied() {
    if (!this.gridApi) return;

    setTimeout(() => {
      this.isPageRestored = false;
      localStorage.setItem(this.PAGE_KEY, '1');

      this.gridApi.paginationGoToFirstPage();
      this.onPaginationChanged();
    }, 100);
  }

  onPaginationChanged() {
    if (!this.gridApi) return;

    const rowCount = this.gridApi.getModel().getRowCount();

    this.currPage = this.gridApi.paginationGetCurrentPage() + 1;
    this.totalPage = this.gridApi.paginationGetTotalPages();
    this.paging = Array.from({ length: this.totalPage }, (_, i) => i + 1);

    this.searchedRow = rowCount;
    this.noRecordsFound = rowCount === 0;

    localStorage.setItem(this.PAGE_KEY, this.currPage.toString());

    rowCount === 0
      ? this.gridApi.showNoRowsOverlay()
      : this.gridApi.hideOverlay();

    this.cdr.markForCheck();
  }

  gotoPage(page: number) {
    this.gridApi.paginationGoToPage(page - 1);
    this.currPage = page;
    localStorage.setItem(this.PAGE_KEY, page.toString());
    this.onPaginationChanged();
  }

  onBtNext() {
    this.gridApi.paginationGoToNextPage();
    this.onPaginationChanged();
  }

  onBtPrevious() {
    this.gridApi.paginationGoToPreviousPage();
    this.onPaginationChanged();
  }

  handleReload() {
    if (!this.gridApi) return;

    this.isPageRestored = false;
    localStorage.removeItem(this.PAGE_KEY);

    this.filtertextbox.nativeElement.value = '';
    this.gridApi.setQuickFilter('');
    this.gridApi.setFilterModel(null);

    this.gridApi.paginationGoToFirstPage();
    this.onPaginationChanged();

    this.reloadTable.emit(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rowData'] && this.gridApi && !this.isPageRestored) {
      const savedPage = Number(localStorage.getItem(this.PAGE_KEY));

      setTimeout(() => {
        const totalPages = this.gridApi.paginationGetTotalPages();

        if (savedPage && savedPage <= totalPages) {
          this.gridApi.paginationGoToPage(savedPage - 1);
          this.currPage = savedPage;
        }

        this.isPageRestored = true;
        this.onPaginationChanged();
        this.handleRowClick();
      }, 0);
    }

    if (changes['currentRowSelectedIndex']) {
      this.handleRowClick();
    }
  }

  handleRowClick() {
    const container = document.querySelector('.ag-center-cols-container');
    if (!container) return;

    const rows = container.querySelectorAll('.ag-row');
    const storedIndex = localStorage.getItem('currentRowIndex');
    const selectedIndex = this.currentRowSelectedIndex || storedIndex;

    rows.forEach((row: any) => {
      row.style.background =
        row.getAttribute('row-index') == selectedIndex ? '#edf5fb' : '#fff';
    });
  }
}
